/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import { cssMap as cssMapUnbounded, css } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { trackChangesMessages } from '@atlaskit/editor-common/messages';
import HistoryIcon from '@atlaskit/icon-lab/core/history';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Breakpoint, useElementBreakpoints } from '../../hooks/useElementBreakpoints';
import messages from './messages';

const styles = cssMap({
	container: {
		flexWrap: 'wrap',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	containerSmall: {
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.200'),
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
	buttonsWrapper: {
		marginLeft: 'auto',
	},
});

const stylesUnbounded = cssMapUnbounded({
	toggleChangesButtonWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'button::after': {
			border: 'none',
		},
	},
});

const chromelessWidth = css({
	width: `calc(100% - ${token('space.300')})`,
	justifySelf: 'center',
});

const chromelessStyles = css({
	width: `calc(100% - ${token('space.300')})`,
	margin: '0 auto',
});

interface AcceptRejectProps {
	isDisplayingChanges?: boolean;
	onAccept: () => void;
	onBadResponseButtonClick: () => void;
	onGoodResponseButtonClick: () => void;
	onReject: () => void;
	onToggleChanges: () => void;
}

export const AcceptRejectScreen = ({
	onAccept,
	onReject,
	onBadResponseButtonClick,
	onGoodResponseButtonClick,
	isDisplayingChanges,
	onToggleChanges,
}: AcceptRejectProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints(Breakpoint.SmallChromeless);
	const isSmallChromeless = breakpoint <= Breakpoint.SmallChromeless;

	return (
		<FloatingContainer
			ref={setBreakpointsElement}
			data-testid="accept-reject-screen"
			rainbowBorderAnimation={false}
			rainbowBorder={false}
			disablePadding
			enableMaxWidth
			isAIFC
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			css={[isSmallChromeless ? chromelessStyles : chromelessWidth]}
		>
			<Flex
				justifyContent="space-between"
				alignItems="center"
				xcss={cx(isSmallChromeless ? styles.containerSmall : styles.container)}
			>
				<Flex gap="space.050">
					<Flex
						alignItems="center"
						justifyContent="center"
						xcss={stylesUnbounded.toggleChangesButtonWrapper}
					>
						<IconButton
							testId="toggle-changes-button"
							type="button"
							icon={HistoryIcon}
							label={formatMessage(trackChangesMessages.toolbarIconLabel)}
							appearance="subtle"
							isTooltipDisabled={false}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							tooltip={{
								position: 'top',
								content: formatMessage(trackChangesMessages.toolbarIconLabel),
							}}
							isSelected={isDisplayingChanges}
							onClick={onToggleChanges}
						/>
					</Flex>
					<FeedbackForm
						onBadResponseButtonClick={onBadResponseButtonClick}
						onGoodResponseButtonClick={onGoodResponseButtonClick}
						flexIconWrapper
						largeIconButton
					/>
				</Flex>
				<Flex gap="space.050" xcss={styles.buttonsWrapper}>
					<Button appearance="subtle" onClick={onReject}>
						{formatMessage(messages.rejectLabel)}
					</Button>
					<Button appearance="primary" onClick={onAccept} autoFocus>
						{formatMessage(messages.acceptLabel)}
					</Button>
				</Flex>
			</Flex>
		</FloatingContainer>
	);
};
